import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";

const LangSwitcher = ({ onNavigate }) => {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || "fr").slice(0, 2);
  return (
    <div data-testid="lang-switcher" className="flex items-center gap-2.5">
      {["fr", "en", "de"].map((lng) => (
        <button
          key={lng}
          data-testid={`lang-switch-${lng}`}
          onClick={() => {
            i18n.changeLanguage(lng);
            onNavigate && onNavigate();
          }}
          aria-pressed={current === lng}
          className={`font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
            current === lng ? "text-brass" : "text-slate2 hover:text-chalk"
          }`}
        >
          {lng}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("common");

  const LINKS = [
    { name: t("nav.accueil"), to: "/", id: "accueil" },
    { name: t("nav.services"), to: "/services", id: "services" },
    { name: t("nav.investissements"), to: "/investissements", id: "investissements" },
    { name: t("nav.portfolio"), to: "/investissements/portfolio", id: "portfolio" },
    { name: t("nav.conformite"), to: "/conformite", id: "conformite" },
    { name: t("nav.cabinet"), to: "/le-cabinet", id: "cabinet" },
    { name: t("nav.equipe"), to: "/equipe", id: "equipe" },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.08] bg-ink/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8 lg:px-12">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
          <Logo size={36} />
          <span className="leading-tight">
            <span className="block whitespace-nowrap font-display text-[14px] tracking-[0.06em] text-chalk">
              GABANA MANAGEMENT SA
            </span>
            <span className="hidden font-mono text-[9px] uppercase tracking-[0.22em] text-slate2 xl:block">
              {t("brandSub")}
            </span>
          </span>
        </Link>

        <nav className="ml-10 hidden items-center gap-5 xl:flex" aria-label="Navigation principale">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-200 hover:text-chalk ${
                  isActive ? "text-brass" : "text-mist/70"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
          <div className="border-l border-white/10 pl-4">
            <LangSwitcher />
          </div>
          <Link to="/contact" data-testid="nav-cta-contact" className="btn-brass !px-4 !py-2.5 whitespace-nowrap">
            {t("nav.cta")}
          </Link>
        </nav>

        <div className="flex items-center gap-5 xl:hidden">
          <LangSwitcher />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="p-2 text-chalk"
            aria-label="Ouvrir le menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/[0.08] bg-ink/95 xl:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="border-b border-white/[0.06] py-3 font-mono text-xs uppercase tracking-[0.2em] text-mist transition-colors duration-200 hover:text-chalk"
                >
                  {l.name}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta-contact"
                onClick={() => setOpen(false)}
                className="btn-brass mt-4 justify-center"
              >
                {t("nav.cta")}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
